import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';
import ConfirmationModal from '/imports/ui/components/common/modal/confirmation/component';

const intlMessages = defineMessages({
  // Cancel modal messages
  title: {
    id: 'app.screenshare.cancelModal.title',
    description: 'Title for screenshare cancel modal',
  },
  description: {
    id: 'app.screenshare.cancelModal.description',
    description: 'Description for screenshare cancel modal',
  },
  retryLabel: {
    id: 'app.screenshare.cancelModal.retryLabel',
    description: 'Retry button label for screenshare cancel modal',
  },
  cancelLabel: {
    id: 'app.screenshare.cancelModal.cancelLabel',
    description: 'Cancel button label for screenshare cancel modal',
  },
  // Manual stop modal messages
  manualStopTitle: {
    id: 'app.screenshare.manualStopModal.title',
    description: 'Title for manual screenshare stop modal',
  },
  manualStopDescription: {
    id: 'app.screenshare.manualStopModal.description',
    description: 'Description for manual screenshare stop modal',
  },
  manualStopRetryLabel: {
    id: 'app.screenshare.manualStopModal.retryLabel',
    description: 'Retry button label for manual screenshare stop modal',
  },
  manualStopCancelLabel: {
    id: 'app.screenshare.manualStopModal.cancelLabel',
    description: 'Cancel button label for manual screenshare stop modal',
  },
  // Mandatory stop modal messages
  mandatoryStopTitle: {
    id: 'app.screenshare.mandatoryStopModal.title',
    description: 'Title for mandatory screenshare stop modal',
  },
  mandatoryStopDescription: {
    id: 'app.screenshare.mandatoryStopModal.description',
    description: 'Description for mandatory screenshare stop modal',
  },
  mandatoryStopOkLabel: {
    id: 'app.screenshare.mandatoryStopModal.okLabel',
    description: 'OK button label for mandatory screenshare stop modal',
  },
});

const propTypes = {
  intl: PropTypes.object.isRequired,
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSkip: PropTypes.func,
  priority: PropTypes.string,
  modalType: PropTypes.oneOf(['cancel', 'manualStop', 'mandatoryStop', 'initial', 'recording']),
  isRecordingActive: PropTypes.bool,
};

const defaultProps = {
  priority: 'low',
  modalType: 'cancel',
  onSkip: null,
  isRecordingActive: false,
};

const ScreenshareCancelModal = ({
  intl,
  isOpen,
  setIsOpen,
  onRetry,
  onCancel,
  onSkip,
  priority,
  modalType,
  isRecordingActive,
}) => {
  const handleRetry = () => {
    onRetry();
    setIsOpen(false);
  };

  const handleCancel = () => {
    onCancel();
    setIsOpen(false);
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
    setIsOpen(false);
  };

  const handleClose = () => {
    // If recording is active, don't allow closing without action
    if (isRecordingActive) {
      return;
    }
    // Otherwise, treat close as skip
    if (onSkip) {
      onSkip();
    } else {
      onCancel();
    }
    // [Fix N6] Ensure modal is closed in local state
    setIsOpen(false);
  };

  // [Fix #9] Use intl.formatMessage for i18n instead of hardcoded English strings
  // [Fix #8] Added mandatoryStop case
  const getMessages = () => {
    // Initial prompt on join (not recording yet)
    if (modalType === 'initial' || (modalType === 'cancel' && !isRecordingActive)) {
      return {
        title: intl.formatMessage(intlMessages.title),
        description: intl.formatMessage(intlMessages.description),
        retryLabel: intl.formatMessage(intlMessages.retryLabel),
        cancelLabel: intl.formatMessage(intlMessages.cancelLabel),
        showSkip: true,
      };
    }

    // [Fix #8] Mandatory stop — recording requires screenshare, restarting automatically
    if (modalType === 'mandatoryStop') {
      return {
        title: intl.formatMessage(intlMessages.mandatoryStopTitle),
        description: intl.formatMessage(intlMessages.mandatoryStopDescription),
        retryLabel: intl.formatMessage(intlMessages.mandatoryStopOkLabel),
        cancelLabel: null,
        showSkip: false,
      };
    }

    // Recording is active - screenshare is mandatory
    if (modalType === 'recording' || isRecordingActive) {
      return {
        title: intl.formatMessage(intlMessages.title),
        description: intl.formatMessage(intlMessages.description),
        retryLabel: intl.formatMessage(intlMessages.retryLabel),
        cancelLabel: null,
        showSkip: false,
      };
    }

    // Manual stop or other cases
    if (modalType === 'manualStop') {
      return {
        title: intl.formatMessage(intlMessages.manualStopTitle),
        description: intl.formatMessage(intlMessages.manualStopDescription),
        retryLabel: intl.formatMessage(intlMessages.manualStopRetryLabel),
        cancelLabel: intl.formatMessage(intlMessages.manualStopCancelLabel),
        showSkip: true,
      };
    }

    // Default fallback
    return {
      title: intl.formatMessage(intlMessages.title),
      description: intl.formatMessage(intlMessages.description),
      retryLabel: intl.formatMessage(intlMessages.retryLabel),
      cancelLabel: intl.formatMessage(intlMessages.cancelLabel),
      showSkip: true,
    };
  };

  const messages = getMessages();

  return (
    <ConfirmationModal
      intl={intl}
      title={messages?.title ?? ''}
      description={messages?.description ?? ''}
      confirmButtonLabel={messages?.retryLabel ?? ''}
      cancelButtonLabel={messages?.showSkip ? messages?.cancelLabel : null}
      onConfirm={handleRetry}
      onCancel={messages?.showSkip ? handleSkip : undefined}
      onRequestClose={handleClose}
      priority={priority}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
    />
  );
};

ScreenshareCancelModal.propTypes = propTypes;
ScreenshareCancelModal.defaultProps = defaultProps;

export default injectIntl(ScreenshareCancelModal); 